import React from 'react';

/**
 * PhaseNotes - Stage 2 explanatory notes renderer (Glass Cockpit redesign).
 *
 * Unified note-card styling shared with McduTutorial's note/step blocks:
 * card bg var(--gc-card), border var(--gc-border-card), radius 12px.
 * Label colour is the only thing that changes per type; body text always
 * uses the body text colour.
 *
 * - INSIGHT = amber  (#F5B041)
 * - CHECK   = green  (#4ADE80)
 * - STEP    = cyan   (#4FD1F5)
 * - NOTE    = body   (#C9D2DF)
 *
 * Props:
 * - notes: array of { type: 'NOTE'|'STEP'|'CHECK'|'INSIGHT', text: string }
 *   (flow_steps docs from GET /api/phases/{slug})
 */
const LABEL_COLORS = {
  NOTE: 'var(--gc-text-body)',
  STEP: 'var(--gc-cyan)',
  CHECK: 'var(--gc-green)',
  INSIGHT: 'var(--gc-amber)',
};

export const renderNote = (note, index) => {
  const type = (note.type || note.step_type || '').toUpperCase();
  const labelColor = LABEL_COLORS[type] || LABEL_COLORS.NOTE;

  return (
    <div
      key={note.id || index}
      className="flex flex-col gap-1.5 rounded-gc-card px-4 py-3.5 bg-glass-card border"
      style={{ borderColor: 'var(--gc-border-card)' }}
      data-testid={`phase-note-${index}`}
    >
      <span
        className="font-plex-mono text-[11px] font-semibold tracking-[0.08em]"
        style={{ color: labelColor }}
      >
        {type || 'NOTE'}
      </span>
      <span className="text-[15px] leading-[1.5]" style={{ color: 'var(--gc-text-body)' }}>
        {note.text || note.content}
      </span>
    </div>
  );
};

const PhaseNotes = (props) => {
  const notes = (props.notes || []).filter(n => (n.type || '').toUpperCase() !== 'ATC');
  if (notes.length === 0) return null;

  const sorted = [...notes].sort((a, b) => (a.order_index || 0) - (b.order_index || 0));

  return (
    <div className="flex flex-col gap-3" data-testid="phase-notes">
      {sorted.map((note, index) => renderNote(note, index))}
    </div>
  );
};

const SPEAKER_COLORS = {
  YOU: 'var(--gc-green)',
  ATC: 'var(--gc-amber)',
};

export const PhaseAtcCard = (props) => {
  const atc = props.atc;
  if (!atc) return null;

  return (
    <div
      className="flex flex-col gap-3 rounded-gc-card px-4 py-4 bg-glass-card border"
      style={{ borderColor: 'var(--gc-border-card)' }}
      data-testid={`phase-atc-card-${atc.id}`}
    >
      <div className="flex flex-col gap-1">
        <span className="font-plex-mono text-[11px] font-semibold tracking-[0.08em]" style={{ color: 'var(--gc-cyan)' }}>
          ATC
        </span>
        <span className="font-barlow-condensed font-semibold text-[17px]" style={{ color: 'var(--gc-text-main)' }}>
          {atc.title}
        </span>
      </div>

      <div className="flex flex-col gap-2">
        {(atc.exchange || []).map((line, i) => (
          <div key={i} className="flex items-start gap-3">
            <span
              className="font-plex-mono text-[11px] font-semibold w-8 shrink-0 pt-0.5"
              style={{ color: SPEAKER_COLORS[line.speaker] || 'var(--gc-text-muted)' }}
            >
              {line.speaker}
            </span>
            <span className="text-[15px] leading-[1.5]" style={{ color: 'var(--gc-text-main)' }}>
              {line.text}
            </span>
          </div>
        ))}
      </div>

      {atc.tip && (
        <p className="text-sm leading-[1.5]" style={{ color: 'var(--gc-text-body)' }}>
          {atc.tip}
        </p>
      )}

      <p className="font-plex-mono text-[11px] tracking-[0.04em]" style={{ color: 'var(--gc-text-muted)' }}>
        EXAMPLE ONLY · USE THE NAMES, SQUAWKS AND FREQUENCIES YOUR ATC GIVES YOU
      </p>
    </div>
  );
};

export default PhaseNotes;
